/**
 * Environment Configuration
 *
 * Centralized environment variable access with validation and type safety.
 * All environment variables must be prefixed with VITE_ to be exposed to the client.
 */

interface EnvConfig {
  // Environment
  NODE_ENV: 'development' | 'production' | 'test'
  IS_DEV: boolean
  IS_PROD: boolean
  IS_TEST: boolean

  // Application
  APP_URL: string
  APP_NAME: string

  // API Configuration (future backend integration)
  API_URL: string
  API_TIMEOUT: number

  // Feature Flags
  ENABLE_ANALYTICS: boolean
  ENABLE_ERROR_TRACKING: boolean
  ENABLE_DEVTOOLS: boolean

  // Third-party Services
  SENTRY_DSN?: string
  ANALYTICS_SITE_ID?: string
  ANALYTICS_DOMAIN?: string

  // Auth0 Configuration
  AUTH0_DOMAIN: string
  AUTH0_CLIENT_ID: string
  AUTH0_AUDIENCE?: string
  AUTH0_REDIRECT_URI: string

  // Development Overrides
  DEV_ROLE_OVERRIDE?: 'owner' | 'manager' | 'teacher' | 'aerialist'
}

/**
 * Parse boolean environment variable
 */
function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined) return defaultValue
  return value === 'true' || value === '1'
}

/**
 * Parse number environment variable
 */
function parseNumber(value: string | undefined, defaultValue: number): number {
  if (value === undefined) return defaultValue
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? defaultValue : parsed
}

/**
 * Get environment variable with fallback
 */
function getEnv(key: string, defaultValue: string = ''): string {
  if (typeof import.meta.env !== 'undefined') {
    return import.meta.env[key] ?? defaultValue
  }
  return defaultValue
}

/**
 * Validated environment configuration
 */
export const env: EnvConfig = {
  // Environment
  NODE_ENV: (getEnv('MODE') || getEnv('NODE_ENV') || 'development') as EnvConfig['NODE_ENV'],
  get IS_DEV() {
    return this.NODE_ENV === 'development'
  },
  get IS_PROD() {
    return this.NODE_ENV === 'production'
  },
  get IS_TEST() {
    return this.NODE_ENV === 'test'
  },

  // Application
  APP_URL: getEnv('VITE_APP_URL', 'http://localhost:3000'),
  APP_NAME: getEnv('VITE_APP_NAME', 'AerialFlow'),

  // API Configuration
  API_URL: getEnv('VITE_API_URL', 'http://localhost:5000/api'),
  API_TIMEOUT: parseNumber(getEnv('VITE_API_TIMEOUT'), 30000),

  // Feature Flags
  ENABLE_ANALYTICS: parseBoolean(
    getEnv('VITE_ENABLE_ANALYTICS'),
    getEnv('MODE') === 'production'
  ),
  ENABLE_ERROR_TRACKING: parseBoolean(
    getEnv('VITE_ENABLE_ERROR_TRACKING'),
    getEnv('MODE') === 'production'
  ),
  ENABLE_DEVTOOLS: parseBoolean(
    getEnv('VITE_ENABLE_DEVTOOLS'),
    getEnv('MODE') === 'development'
  ),

  // Third-party Services
  SENTRY_DSN: getEnv('VITE_SENTRY_DSN') || undefined,
  ANALYTICS_SITE_ID: getEnv('VITE_ANALYTICS_SITE_ID') || undefined,
  ANALYTICS_DOMAIN: getEnv('VITE_ANALYTICS_DOMAIN') || undefined,

  // Auth0 Configuration
  AUTH0_DOMAIN: getEnv('VITE_AUTH0_DOMAIN', ''),
  AUTH0_CLIENT_ID: getEnv('VITE_AUTH0_CLIENT_ID', ''),
  AUTH0_AUDIENCE: getEnv('VITE_AUTH0_AUDIENCE') || undefined,
  AUTH0_REDIRECT_URI: getEnv('VITE_AUTH0_REDIRECT_URI', 'http://localhost:3000/auth/callback'),

  // Development Overrides (only used in dev mode)
  DEV_ROLE_OVERRIDE: getEnv('VITE_DEV_ROLE_OVERRIDE') as EnvConfig['DEV_ROLE_OVERRIDE'] || undefined,
}

/**
 * Validate required environment variables
 */
export function validateEnv(): void {
  const errors: string[] = []

  // Auth0 is required in all environments
  if (!env.AUTH0_DOMAIN) {
    errors.push('VITE_AUTH0_DOMAIN is required')
  }
  if (!env.AUTH0_CLIENT_ID) {
    errors.push('VITE_AUTH0_CLIENT_ID is required')
  }

  // Production-only validations
  if (env.IS_PROD) {
    if (env.ENABLE_ERROR_TRACKING && !env.SENTRY_DSN) {
      errors.push('VITE_SENTRY_DSN is required when error tracking is enabled')
    }

    if (env.ENABLE_ANALYTICS && (!env.ANALYTICS_SITE_ID || !env.ANALYTICS_DOMAIN)) {
      errors.push('VITE_ANALYTICS_SITE_ID and VITE_ANALYTICS_DOMAIN are required when analytics is enabled')
    }

    if (!env.APP_URL.startsWith('https://')) {
      errors.push('VITE_APP_URL must use HTTPS in production')
    }

    if (!env.AUTH0_REDIRECT_URI.startsWith('https://')) {
      errors.push('VITE_AUTH0_REDIRECT_URI must use HTTPS in production')
    }
  }

  if (errors.length > 0) {
    const message = `Environment validation failed:\n${errors.map(e => `  - ${e}`).join('\n')}`

    if (env.IS_PROD) {
      // In production, fail fast
      throw new Error(message)
    } else {
      // In development, warn but continue
      console.warn(message)
    }
  }
}

/**
 * Log environment configuration (safe for production)
 */
export function logEnvConfig(): void {
  if (env.IS_TEST) return // Don't log in tests

  const safeConfig = {
    NODE_ENV: env.NODE_ENV,
    APP_URL: env.APP_URL,
    APP_NAME: env.APP_NAME,
    API_URL: env.API_URL,
    ENABLE_ANALYTICS: env.ENABLE_ANALYTICS,
    ENABLE_ERROR_TRACKING: env.ENABLE_ERROR_TRACKING,
    ENABLE_DEVTOOLS: env.ENABLE_DEVTOOLS,
    // Redact sensitive values
    SENTRY_DSN: env.SENTRY_DSN ? '***' : undefined,
    ANALYTICS_SITE_ID: env.ANALYTICS_SITE_ID ? '***' : undefined,
    // Auth0 (domain is not sensitive, client ID is public for SPAs)
    AUTH0_DOMAIN: env.AUTH0_DOMAIN,
    AUTH0_REDIRECT_URI: env.AUTH0_REDIRECT_URI,
  }

  console.log('[env] Configuration loaded:', safeConfig)
}

// Validate on module load
validateEnv()
